import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '~/lib/utils'

// 미니멀 페이지네이션 — 작은 페이지 버튼 + Chevron prev/next. 시맨틱 토큰만 사용.
// offset 기반(0-base) API(total/pageSize/offset/onOffset)는 그대로 유지한다.

export interface PaginationProps {
    /** 전체 항목 수 */
    total: number
    /** 페이지당 항목 수 */
    pageSize: number
    /** 현재 0-base offset */
    offset: number
    /** offset 변경 콜백 */
    onOffset: (offset: number) => void
    /** 로딩 등으로 비활성 */
    disabled?: boolean
    /** 현재 페이지 양옆에 노출할 번호 개수 */
    siblingCount?: number
}

const DOTS = 'dots'

// 노출할 페이지 토큰 목록(번호 또는 생략 …)을 만든다. page는 1-base.
function buildPages(
    page: number,
    pageCount: number,
    siblingCount: number
): Array<number | typeof DOTS> {
    // 끝 + 양옆 창 + 첫/마지막 + dots 2개를 모두 보일 수 있으면 전부 노출.
    const totalSlots = siblingCount * 2 + 5
    if (pageCount <= totalSlots) {
        return Array.from({ length: pageCount }, (_, i) => i + 1)
    }

    const left = Math.max(page - siblingCount, 1)
    const right = Math.min(page + siblingCount, pageCount)
    const showLeftDots = left > 2
    const showRightDots = right < pageCount - 1

    const pages: Array<number | typeof DOTS> = [1]
    if (showLeftDots) pages.push(DOTS)
    for (
        let p = showLeftDots ? left : 2;
        p <= (showRightDots ? right : pageCount - 1);
        p++
    ) {
        pages.push(p)
    }
    if (showRightDots) pages.push(DOTS)
    pages.push(pageCount)
    return pages
}

const navBtn =
    'flex size-7 items-center justify-center rounded-[var(--radius)] text-foreground transition-colors hover:bg-secondary disabled:pointer-events-none disabled:opacity-40'

export function Pagination({
    total,
    pageSize,
    offset,
    onOffset,
    disabled = false,
    siblingCount = 1,
}: PaginationProps) {
    const pageCount = Math.max(1, Math.ceil(total / pageSize))
    const page = Math.floor(offset / pageSize) + 1
    if (pageCount <= 1) return null

    const goto = (p: number) => onOffset((p - 1) * pageSize)
    const pages = buildPages(page, pageCount, siblingCount)

    const prevDisabled = disabled || page <= 1
    const nextDisabled = disabled || page >= pageCount

    return (
        <nav
            aria-label='페이지네이션'
            className='flex flex-col items-center gap-2'>
            <div className='flex items-center gap-1'>
                <button
                    type='button'
                    className={navBtn}
                    aria-label='이전 페이지'
                    disabled={prevDisabled}
                    onClick={() => goto(page - 1)}>
                    <ChevronLeft className='size-4' />
                </button>

                {pages.map((p, i) =>
                    p === DOTS ? (
                        <span
                            key={`dots-${i}`}
                            aria-hidden='true'
                            className='text-muted-foreground flex size-7 items-center justify-center text-[13px]'>
                            …
                        </span>
                    ) : (
                        <button
                            key={p}
                            type='button'
                            className={cn(
                                'flex size-7 items-center justify-center rounded-[var(--radius)] text-[13px] transition-colors',
                                p === page
                                    ? 'bg-primary text-primary-foreground'
                                    : 'text-foreground hover:bg-secondary'
                            )}
                            aria-current={p === page ? 'page' : undefined}
                            disabled={disabled}
                            onClick={() => goto(p)}>
                            {p === page ? (
                                <span className='sr-only'>현재페이지 </span>
                            ) : null}
                            {p}
                        </button>
                    )
                )}

                <button
                    type='button'
                    className={navBtn}
                    aria-label='다음 페이지'
                    disabled={nextDisabled}
                    onClick={() => goto(page + 1)}>
                    <ChevronRight className='size-4' />
                </button>
            </div>
            <p className='text-muted-foreground text-[12px]'>
                전체 {pageCount}페이지
            </p>
        </nav>
    )
}
